import { asadoSdk } from '@fff-web/asado-sdk';
import { type Env } from '@fff-web/constants';
import { IUser } from '@fff-web/fff-utilities';
import { getHomepageHost, getHostName } from '../../utils/getEnv';
import { getHost, getPathname } from '../../utils/location';

type ChatbotContext = Record<string, string | number | boolean | string[]>;

/**
 * Gets the clean user context for the chatbot
 */
export const flattenUserInfo = (user: IUser): Record<string, string | number | boolean> => ({
  userId: user.userId ?? '',
  firstName: user.firstName,
  lastName: user.lastName,
  phoneNumber: user.phoneNumber ?? '',
  role: user.role,
  shippingCountry: user.shippingCountry,
  shippingState: user.shippingState,
  shippingZipCode: user.shippingZipCode,
  shippingCity: user.shippingCity,
  subscriptionEndDate: user.subscriptionEndDate,
  subscriptionStartDate: user.subscriptionStartDate,
  subscriptionState: user.subscriptionState,
  subscriptionType: user.subscriptionType,
  email: user.email,
  planCode: user.planCode,
  uuid: user.uuid ?? '',
});

/**
 * Maps the footer host segment (from {@link getHostName}) to the asado SDK environment.
 * The SDK resolves the asado host from this env, so env and host always stay consistent.
 * The footer's `staging` env has no asado equivalent, so anything unmapped falls back to `dev`.
 */
const HOSTNAME_TO_ENV: Record<string, Env> = {
  fabfitfun: 'prod',
  'fff-qa': 'qa',
  'fff-dev': 'dev',
};

/**
 * Fetches the chatbot metadata (address, orders, tracking, credits) from the asado BFF
 * `GET /bff/v1/chatbot/context` and maps it into the chatbot `context` shape, dropping
 * null/undefined values.
 *
 * Uses `withMenuUserData=false` because identity comes from the user context. Resolves to an empty object on any failure — including the `401` returned for
 * anonymous users — so the widget keeps working regardless of auth state.
 */
export const fetchChatbotMetadata = async (): Promise<ChatbotContext> => {
  try {
    const env = HOSTNAME_TO_ENV[getHostName()] ?? 'dev';

    const response = await asadoSdk(env, true).getChatbotContext({
      withMenuUserData: false,
    });

    // `response.data` is the asado envelope; the payload is `response.data.data`.
    if (!response.ok || !response.data.data) return {};

    const context: ChatbotContext = {};
    for (const [key, value] of Object.entries(response.data.data)) {
      if (value === null || value === undefined) continue;
      context[key] = value;
    }
    return context;
  } catch {
    return {};
  }
};

/**
 * Gets the current path for the chat
 * TODO: This is a temporary solution, we should use a more dynamic approach once we finish the migration
 */
const getCurrentPathForChat = (): string => {
  let currentPath = getPathname() || '';

  if (currentPath !== '/') {
    if (currentPath.endsWith('/')) {
      currentPath = currentPath.substr(0, currentPath.length - 1);
    }
  }

  if (currentPath.length === 0 && getHost() === getHomepageHost()) {
    currentPath = '/homepage';
  }

  return currentPath;
};

/**
 * Checks if the current path is allowed to load the chat
 * TODO: This is a temporary solution, we should use a more dynamic approach once we finish the migration
 */
export const allowedChatPaths = [
  '/cancel-subscription',
  '/reactivate-my-membership',
  '/upgrade-confirmation',
  '/homepage',
  '/my-subscriptions',
  '/customize',
  '/my-account',
  '/my-account/orders',
  '/my-account/rate-and-review',
  '/shop',
  '/invite-friends',
  '/',
];

export const isChatPathAllowed = (): boolean => {
  const path = getCurrentPathForChat();
  const isSupport = getHost().includes('support');
  const isTracking = getPathname().includes('track-shipment');
  const isLogin = getPathname().includes('login');

  return (allowedChatPaths.includes(path) || isSupport || isTracking) && !isLogin;
};
